import { Badge, Divider, Modal, Spinner, Text } from '@thefarmersfront/kitchen';

import { ModalContentsContainer, ModalLabelValueRow, ModalSection } from '~/components/common';

import { useGetReturnDisposalDetailQuery } from '~/hooks/receive';
import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  RETURN_DISPOSAL_PROCESS_METHOD,
  RETURN_DISPOSAL_PROCESS_STATUS,
  RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR,
} from '~/constants';

import ReturnDisposalStatusHistorySection from './ReturnDisposalStatusHistorySection';

import type {
  TypeReturnDisposalDetailApi,
  TypeReturnDisposalDetailRtsApi,
  TypeReturnDisposalDetailRtsRentalApi,
} from '~/types/receive.types';

import styles from './ReceiveReturnDisposalDetailModal.module.scss';

type TypeReceiveReturnDisposalDetailModalProps = {
  isOpen: boolean;
  inspectionId: number;
  onClose: () => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 이슈 상세 모달
 *
 * @note 기본 정보 + (처리방법별 조건부 섹션) + 상태 변경 이력.
 */
const ReceiveReturnDisposalDetailModal = ({
  isOpen,
  inspectionId,
  onClose,
}: TypeReceiveReturnDisposalDetailModalProps) => {
  const { data: detail, isLoading, isError } = useGetReturnDisposalDetailQuery({ inspectionId, enabled: isOpen });

  return (
    <Modal title="이슈 상세" size="xlarge" open={isOpen} onConfirm={onClose}>
      {isLoading ? (
        <div className={styles['state-area']}>
          <Spinner />
        </div>
      ) : isError || !detail ? (
        <div className={styles['state-area']}>
          <Text size="large" color="tertiary">
            상세 정보를 불러오지 못했습니다.
          </Text>
        </div>
      ) : (
        <ModalContentsContainer className={styles['modal-body']}>
          <BasicInfoSection detail={detail} />
          {detail.processMethod === 'RTS' && detail.rts && (
            <>
              <RtsInfoSection rts={detail.rts} />
              {detail.rts.rtsMethod === 'DIRECT' && detail.rts.rentals && detail.rts.rentals.length > 0 && (
                <PalletInfoSection rentals={detail.rts.rentals} />
              )}
            </>
          )}
          {detail.processMethod === 'SCRAP' && <ScrapInfoSection scrapFee={detail.scrap?.scrapFee ?? null} />}
          <ReturnDisposalStatusHistorySection inspectionId={inspectionId} isEnabled={isOpen} />
        </ModalContentsContainer>
      )}
    </Modal>
  );
};

export default ReceiveReturnDisposalDetailModal;

/** 기본 정보 (항상 노출) */
const BasicInfoSection = ({ detail }: { detail: TypeReturnDisposalDetailApi }) => (
  <ModalSection title="기본 정보">
    <div className={styles['info-grid']}>
      <ModalLabelValueRow label="입고요청번호" value={detail.purchaseOrderCode} />
      <ModalLabelValueRow label="이슈발생센터" value={detail.issuedWarehouseName} />
      <ModalLabelValueRow label="상품명" value={detail.goodsName} />
      <ModalLabelValueRow label="상품코드" value={detail.goodsCode} />
      <ModalLabelValueRow label="화주사" value={detail.ownerName ?? ''} />
      <ModalLabelValueRow label="공급사" value={detail.supplierName ?? ''} />
      <ModalLabelValueRow label="이슈상세" value={detail.issueType} />
      <ModalLabelValueRow label="이슈수량" value={formatNumber(detail.defectQty)} />
      <ModalLabelValueRow
        label="이슈등록일"
        value={formatDate(detail.issueRegisteredAt, DATE_FORMAT.DATE_TIME) ?? ''}
      />
      <ModalLabelValueRow
        label="자동폐기 예정일"
        value={formatDate(detail.scrapPlannedAt, DATE_FORMAT.DATE_TIME) ?? ''}
      />
      <ModalLabelValueRow label="처리방법" value={RETURN_DISPOSAL_PROCESS_METHOD[detail.processMethod]} />
      <ModalLabelValueRow
        label="처리상태"
        value={
          <span className={styles['badge-cell']}>
            <Badge variant="light" status color={RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR[detail.processStatus]}>
              {RETURN_DISPOSAL_PROCESS_STATUS[detail.processStatus]}
            </Badge>
          </span>
        }
      />
    </div>
  </ModalSection>
);

/** 회수 정보 (회송 — 택배/직배 조건부) */
const RtsInfoSection = ({ rts }: { rts: TypeReturnDisposalDetailRtsApi }) => {
  const methodLabel = rts.rtsMethod === 'PARCEL' ? '택배' : rts.rtsMethod === 'DIRECT' ? '직배' : '';
  const invoiceNos = rts.parcel?.invoiceNos ?? [];

  return (
    <ModalSection title="회수 정보">
      <ModalLabelValueRow alignItems="start" label="회수지 주소" value={rts.inboundAddress ?? ''} />
      <div className={styles['info-grid']}>
        {rts.rtsMethod === 'DIRECT' ? (
          <>
            <ModalLabelValueRow label="회수방법" value={methodLabel} />
            <ModalLabelValueRow
              label="회수예정일시"
              value={formatDate(rts.rtsPlannedAt, DATE_FORMAT.DATE_TIME) ?? ''}
            />
            <ModalLabelValueRow label="파렛트 반출" value={rts.palletReleased ? 'Y' : 'N'} />
          </>
        ) : (
          <>
            <ModalLabelValueRow label="회수방법" value={methodLabel} />
            <ModalLabelValueRow label="택배사" value={rts.parcel?.courierName ?? ''} />
            <ModalLabelValueRow label="회수예정일" value={formatDate(rts.rtsPlannedAt, DATE_FORMAT.DATE) ?? ''} />
            <ModalLabelValueRow
              label="송장번호"
              value={
                invoiceNos.length ? (
                  <div className={styles['invoice-list']}>
                    {invoiceNos.map((invoiceNo) => (
                      <Text key={invoiceNo} size="large" element="span">
                        {invoiceNo}
                      </Text>
                    ))}
                  </div>
                ) : (
                  '-'
                )
              }
            />
          </>
        )}
      </div>
    </ModalSection>
  );
};

/** 파렛트 정보 (직배 — 렌탈사별 카드 반복, 최대 2개) */
const PalletInfoSection = ({ rentals }: { rentals: TypeReturnDisposalDetailRtsRentalApi[] }) => (
  <ModalSection title="파렛트 정보">
    {rentals.map((rental, index) => (
      <div key={`${rental.rentalCompanyCode}-${rental.businessCode}`}>
        {index > 0 && (
          <div className={styles['rental-divider']}>
            <Divider orientation="horizontal" color="gray200" />
          </div>
        )}
        <div className={styles['info-grid']}>
          <ModalLabelValueRow label="렌탈사" value={rental.rentalCompanyName ?? ''} />
          <ModalLabelValueRow label="업체코드" value={rental.businessCode ?? ''} />
          <ModalLabelValueRow label="담당자명" value={rental.contactName ?? ''} />
          <ModalLabelValueRow label="담당자 연락처" value={rental.contactPhone ?? ''} />
          <ModalLabelValueRow label="반출 상세 주소" value={rental.releaseAddress ?? ''} />
        </div>
      </div>
    ))}
  </ModalSection>
);

/** 폐기 정보 (폐기 조건부) */
const ScrapInfoSection = ({ scrapFee }: { scrapFee: number | null }) => (
  <ModalSection title="폐기 정보">
    <div className={styles['info-grid']}>
      <ModalLabelValueRow label="폐기비용" value={scrapFee != null ? `₩${formatNumber(scrapFee)}` : ''} />
    </div>
  </ModalSection>
);
